import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from './ui/sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { GraduationCap, Mail, Lock, ArrowRight, Sparkles } from 'lucide-react';
import { PasswordRules, passwordValid } from './StudentPasswordRules';

const errText = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg).filter(Boolean).join(' ') || fallback;
  return fallback;
};

// Use production URL on production, env var elsewhere
const API_URL = (typeof window !== 'undefined' && window.location.hostname === 'app.salesqualifyd.com')
  ? 'https://app.salesqualifyd.com/api'
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StudentLogin() {
  const { orgId, courseSlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // login | magic | forgot | set-password
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState({});
  const [magicSent, setMagicSent] = useState(false);
  const isReset = searchParams.get('reset') === '1';

  const saveSession = (data) => {
    localStorage.setItem('lms_token', data.token);
    localStorage.setItem('lms_student', JSON.stringify(data.student));
    navigate(courseSlug ? `/${orgId}/courses/${courseSlug}` : `/${orgId}/courses`);
  };

  // Check for magic login token in URL
  useEffect(() => {
    const token = searchParams.get('token');
    const action = window.location.pathname.includes('set-password') ? 'set-password' : 
                   window.location.pathname.includes('magic-login') ? 'magic' : null;
    if (token && action === 'magic') {
      handleMagicLogin(token);
    } else if (token && action === 'set-password') {
      setMode('set-password');
    }
  }, [searchParams]);

  // Fetch branding + set custom favicon and page title
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const res = await axios.get(`${API_URL}/lms/public/${orgId}/courses`);
        const b = res.data.branding || {};
        setBranding(b);
        // Custom page title (NOT "Salesqualifyd")
        if (b.page_title) document.title = b.page_title;
        else if (b.sender_name) document.title = b.sender_name + ' — Learning Portal';
        // Custom favicon
        if (b.favicon_url) {
          let link = document.querySelector("link[rel~='icon']");
          if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
          link.href = b.favicon_url;
          let apple = document.querySelector("link[rel='apple-touch-icon']");
          if (!apple) { apple = document.createElement('link'); apple.rel = 'apple-touch-icon'; document.head.appendChild(apple); }
          apple.href = b.favicon_url;
        }
      } catch {}
    };
    fetchBranding();
  }, [orgId]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/lms/student/login`, {
        email: email.toLowerCase().trim(),
        password,
        organization_id: orgId,
      });
      toast.success('Welcome back!');
      saveSession(res.data);
    } catch (err) {
      toast.error(errText(err, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLinkRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/lms/student/${mode === 'forgot' ? 'forgot-password' : 'request-magic-link'}`, {
        email: email.toLowerCase().trim(),
        organization_id: orgId,
      });
      setMagicSent(true);
    } catch (err) {
      toast.error(errText(err, 'Could not send the email. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLogin = async (token) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/lms/student/magic-login?token=${encodeURIComponent(token)}`);
      toast.success('Logged in!');
      saveSession(res.data);
    } catch (err) {
      toast.error(errText(err, 'Invalid or expired link'));
      setMode('login');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    const token = searchParams.get('token');
    if (!token || !passwordValid(password) || password !== confirm) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/lms/student/set-password`, { token, password });
      toast.success(isReset ? 'Password changed. You are now logged in.' : 'Password set. Welcome!');
      saveSession(res.data);
    } catch (err) {
      toast.error(errText(err, 'Failed to set password'));
    } finally {
      setLoading(false);
    }
  };

  const brandColor = branding.brand_color || '#2563eb';

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
      <Toaster />
      <div className="w-full max-w-md bg-white border border-zinc-200 shadow-sm">
        {/* Brand Header */}
        <div className="p-6 text-center border-b border-zinc-200" style={{ borderBottomColor: brandColor }}>
          {branding.brand_logo_url ? (
            <img src={branding.brand_logo_url} alt="" className="h-12 mx-auto mb-3" />
          ) : (
            <GraduationCap className="h-10 w-10 mx-auto mb-3" style={{ color: brandColor }} />
          )}
          <h1 className="text-xl font-bold text-zinc-900">{branding.sender_name || 'Learning Portal'}</h1>
          {branding.login_page_message && (
            <p className="text-sm text-zinc-500 mt-1">{branding.login_page_message}</p>
          )}
        </div>

        <div className="p-6">
          {mode === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="student-email-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                  <Input
                    type="password"
                    placeholder="Your password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="student-password-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full text-white"
                style={{ backgroundColor: brandColor }}
                data-testid="student-login-btn"
              >
                {loading ? 'Logging in...' : 'Log In'} <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
              <div className="flex justify-between text-sm">
                <button
                  type="button"
                  onClick={() => { setMode('forgot'); setMagicSent(false); }}
                  className="hover:underline"
                  style={{ color: brandColor }}
                  data-testid="switch-to-forgot-password"
                >
                  Forgot password?
                </button>
                <button
                  type="button"
                  onClick={() => { setMode('magic'); setMagicSent(false); }}
                  className="hover:underline"
                  style={{ color: brandColor }}
                  data-testid="switch-to-magic-link"
                >
                  <Sparkles className="h-3.5 w-3.5 inline mr-1" /> Email me a login link
                </button>
              </div>
            </form>
          )}

          {(mode === 'magic' || mode === 'forgot') && !magicSent && (
            <form onSubmit={handleMagicLinkRequest} className="space-y-4">
              <p className="text-sm text-zinc-600">
                {mode === 'forgot'
                  ? "Enter your email and we'll send you a link to choose a new password."
                  : "Enter your email and we'll send you a login link — no password needed."}
              </p>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1">Email</label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  data-testid="magic-email-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full text-white"
                style={{ backgroundColor: brandColor }}
                data-testid="send-magic-link-btn"
              >
                {loading ? 'Sending...' : (mode === 'forgot' ? 'Send reset link' : 'Send login link')}
              </Button>
              <div className="text-center">
                <button type="button" onClick={() => setMode('login')} className="text-sm text-zinc-500 hover:underline">
                  Back to password login
                </button>
              </div>
            </form>
          )}

          {(mode === 'magic' || mode === 'forgot') && magicSent && (
            <div className="text-center py-4" data-testid="link-sent-message">
              <Mail className="h-10 w-10 mx-auto mb-3" style={{ color: brandColor }} />
              <h3 className="font-semibold text-zinc-900 mb-2">Check your email</h3>
              <p className="text-sm text-zinc-500">If <strong>{email}</strong> has an account here, we've sent it a link. It works once{mode === 'forgot' ? ' and expires in 1 hour' : ' and expires in 24 hours'}.</p>
              <button onClick={() => { setMagicSent(false); setMode('login'); }} className="mt-4 text-sm text-zinc-500 hover:underline">
                Back to login
              </button>
            </div>
          )}

          {mode === 'set-password' && (
            <form onSubmit={handleSetPassword} className="space-y-4">
              <p className="text-sm text-zinc-600">{isReset ? 'Choose a new password for your account.' : 'Choose a password to access your courses.'}</p>
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1">New Password</label>
                <Input
                  type="password"
                  placeholder="Choose a password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  maxLength={128}
                  data-testid="set-password-input"
                />
              </div>
              <PasswordRules password={password} />
              <div>
                <label className="text-sm font-medium text-zinc-700 block mb-1">Confirm Password</label>
                <Input
                  type="password"
                  placeholder="Type it again"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  required
                  autoComplete="new-password"
                  data-testid="confirm-password-input"
                />
                {confirm && confirm !== password && (
                  <p className="text-xs text-red-600 mt-1" data-testid="password-mismatch">Passwords don't match</p>
                )}
              </div>
              <Button
                type="submit"
                disabled={loading || !passwordValid(password) || password !== confirm}
                className="w-full text-white"
                style={{ backgroundColor: brandColor }}
                data-testid="set-password-btn"
              >
                {loading ? 'Setting...' : 'Set Password'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
